<template>
  <v-card outlined id="performancesTable">
    <v-card-title class="align-start">
      <v-row>
        <v-col cols="12">Données récoltées</v-col>
      </v-row>

      <dot-menu
        :isLoading="page.loading"
        @exportExcel="exportExcel"
        :excelButton="{
          excelData: excelButton.data,
          filename: `suivi-prod-ia-${startDate}_${endDate}.xlsx`,
          disabled: exportDisabled,
        }"
      />
    </v-card-title>

    <v-card-title class="align-start">
      <v-row>
        <v-col cols="12">
          <table-stats :data="stats"></table-stats>
        </v-col>
        <v-col cols="12">
          <ia-vs-human-card
            :data="page.iaVsHuman"
            :loading="page.loadingIaVsHuman"
            :exporting="page.exportingIaVsHuman"
            :exportingAnnual="page.exportingIaVsHumanAnnual"
            :excelData="iaVsHumanExcel.data"
            :excelFilename="`comparatif-ia-humains-par-site-${startDate}_${endDate}.xlsx`"
            @exportExcel="exportIaVsHumanExcel"
            @exportAnnualExcel="exportIaVsHumanAnnualExcel"
          />

          <v-overlay
            :value="page.exportingIaVsHumanAnnual"
            z-index="9999"
            style="position: fixed;"
          >
            <div class="text-center">
              <v-progress-circular indeterminate size="64" color="primary" />
              <div class="mt-3 text-h6 text-white font-weight-bold">
                Export Excel annuel en cours...
              </div>
              <div class="caption text-white">
                Veuillez patienter pendant la génération du fichier.
              </div>
            </div>
          </v-overlay>
        </v-col>
      </v-row>
    </v-card-title>

    <v-card-text>
      <v-alert dense outlined type="info" class="caption mb-4">
        <span class="font-weight-bold">
          Les deux colonnes ne sont pas sur la même date.
        </span>
        « Articles générés », et son détail publication directe / brouillons /
        programmés, est daté sur la <b>date de mise en file dans RMR IA</b>, pas sur
        la publication. « Articles publiés » est daté sur la
        <b>date de mise en ligne effective</b> relevée dans le feed du site.
        <br />
        Pour les articles en <b>publication directe</b>, les deux dates coïncident
        quasiment toujours. L'écart vient des <b>brouillons</b> (mis en ligne
        manuellement par la rédaction, à une date inconnue de RMR IA) et des
        <b>programmés</b> : environ 21 % des générations arrivent en ligne un autre
        jour que celui de leur mise en file. Le ratio publiés / générés d'une journée
        donnée est donc indicatif, et n'a de sens que cumulé sur la période.
      </v-alert>

      <v-data-table
        :headers="headers"
        :items="page.items"
        :loading="page.loading"
        hide-default-header
        hide-default-footer
        disable-pagination
        disable-filtering
      >
        <template v-slot:header="{ props: headerProps, on: headerOn }">
          <thead class="v-data-table-header">
            <tr>
              <th
                v-for="cell in headerRows.top"
                :key="cell.key"
                :colspan="cell.colspan"
                :rowspan="cell.rowspan"
                :class="[
                  ...headerCellClass(cell, headerProps),
                  cell.grouped ? 'text-center' : '',
                ]"
                @click="cell.value && headerOn.sort(cell.value)"
              >
                {{ cell.text }}
                <v-icon v-if="cell.value" class="v-data-table-header__icon" small>
                  {{ sortIcon }}
                </v-icon>
              </th>
            </tr>
            <tr>
              <th
                v-for="cell in headerRows.sub"
                :key="cell.key"
                :class="headerCellClass(cell, headerProps)"
                @click="headerOn.sort(cell.value)"
              >
                {{ cell.text }}
                <v-icon class="v-data-table-header__icon" small>
                  {{ sortIcon }}
                </v-icon>
              </th>
            </tr>
          </thead>
        </template>
      </v-data-table>
    </v-card-text>

    <progress-bar :download="download" />
  </v-card>
</template>

<style scoped></style>

<script>
import { computed, reactive, watch } from "@vue/composition-api";
import { mdiArrowUp } from "@mdi/js";

import useAxios from "@/hooks/useAxios";
import useProxy from "@/hooks/useProxy";
import { formatCurrency, formatNumber, formatPercent } from "@/utils/formatting";

import DotMenu from "@/components/common/menus/DotMenu";
import ProgressBar, {
  download_initial_state,
  resetDownload,
} from "@/components/common/ProgressBar";
import TableStats from "@/components/ai-factory/suivi-prod-ia/cards/TableStatsCard";
import IaVsHumanCard from "@/components/ai-factory/suivi-prod-ia/cards/IaVsHumanCard";

// Clés à afficher en pourcentage plutôt qu'en nombre brut.
export const RATIO_KEYS = ["post_draft_ratio"];

// Compteurs de génération : renvoyés à null par le back quand un filtre rédacteur
// est actif (aif_post_generation n'a pas de dimension rédacteur).
const GENERATED_KEYS = [
  "nb_posts_generated",
  "nb_posts_generated_publish",
  "nb_posts_generated_draft",
  "nb_posts_generated_future",
];

const HEADERS = [
  {
    text: "Date",
    value: "date",
  },
  {
    text: "Nb. articles publiés",
    value: "nb_posts_published",
  },
  {
    text: "dont republiés",
    value: "nb_posts_republished",
  },
  {
    text: "Nb. articles générés",
    value: "nb_posts_generated",
  },
  {
    text: "dont publication directe",
    value: "nb_posts_generated_publish",
  },
  {
    text: "dont brouillons",
    value: "nb_posts_generated_draft",
  },
  {
    text: "dont programmés",
    value: "nb_posts_generated_future",
  },
  {
    text: "Visites Wysistat",
    value: "wysistat_visits",
  },
  {
    text: "Vues Wysistat",
    value: "wysistat_views",
  },
  {
    text: "Unique Users Marfeel",
    value: "unique_users",
  },
  {
    text: "Sessions GA",
    value: "sessions",
  },
  {
    text: "CA",
    value: "ca",
  },
  {
    text: "Impressions Google search",
    value: "gsc_search_prints",
  },
  {
    text: "Impressions Google news",
    value: "gsc_news_prints",
  },
  {
    text: "Impressions Discover",
    value: "gsc_discover_prints",
  },
  {
    text: "Clics Google search",
    value: "gsc_search_clics",
  },
  {
    text: "Clics Google news",
    value: "gsc_news_clics",
  },
  {
    text: "Clics Discover",
    value: "gsc_discover_clics",
  },
];

// Colonnes regroupees sous un en-tete commun, sur deux lignes. L'ordre doit
// suivre celui de HEADERS : les cellules de la 2e ligne se placent dans les
// emplacements laisses libres par les en-tetes en rowspan.
const COLUMN_GROUPS = [
  {
    text: "Articles publiés",
    columns: [
      { value: "nb_posts_published", text: "Total" },
      { value: "nb_posts_republished", text: "dont republiés" },
    ],
  },
  {
    text: "Articles générés",
    columns: [
      { value: "nb_posts_generated", text: "Total" },
      { value: "nb_posts_generated_publish", text: "dont publication directe" },
      { value: "nb_posts_generated_draft", text: "dont brouillons" },
      { value: "nb_posts_generated_future", text: "dont programmés" },
    ],
  },
];

const buildHeaderRows = (headers) => {
  const top = [];
  const sub = [];
  let index = 0;

  while (index < headers.length) {
    const header = headers[index];
    const group = COLUMN_GROUPS.find(
      (candidate) => candidate.columns[0].value === header.value
    );

    if (group) {
      top.push({
        key: group.text,
        text: group.text,
        colspan: group.columns.length,
        grouped: true,
      });
      sub.push(...group.columns.map((column) => ({ ...column, key: column.value })));
      index += group.columns.length;
    } else {
      top.push({
        key: header.value,
        text: header.text,
        value: header.value,
        rowspan: 2,
      });
      index += 1;
    }
  }

  return { top, sub };
};

export const STATS_DEFAULT = {
  nb_posts_published: 0,
  nb_posts_republished: 0,
  nb_posts_generated: 0,
  nb_posts_generated_publish: 0,
  nb_posts_generated_draft: 0,
  nb_posts_generated_future: 0,
  avg_posts_per_day: 0,
  post_draft_ratio: 0,
  wysistat_visits: 0,
  wysistat_views: 0,
  unique_users: 0,
  sessions: 0,
  ca: 0,
  gsc_search_prints: 0,
  gsc_news_prints: 0,
  gsc_discover_prints: 0,
  gsc_search_clics: 0,
  gsc_news_clics: 0,
  gsc_discover_clics: 0,
};

export default {
  name: "ResultsTable",
  components: {
    DotMenu,
    IaVsHumanCard,
    ProgressBar,
    TableStats,
  },
  setup(props, { emit }) {
    const { store } = useProxy();
    const { axiosGet } = useAxios();

    const page = reactive({
      loading: false,
      loadingIaVsHuman: false,
      exportingIaVsHuman: false,
      exportingIaVsHumanAnnual: false,
      items: [],
      iaVsHuman: {},
    });

    const iaVsHumanExcel = reactive({
      data: new Blob([""], { type: "text/plain" }),
    });
    const download = reactive(download_initial_state());

    const stats = reactive({ ...STATS_DEFAULT });

    const excelButton = reactive({
      data: new Blob([""], { type: "text/plain" }),
    });

    const headers = HEADERS;
    const headerRows = buildHeaderRows(HEADERS);

    const headerCellClass = (cell, headerProps) => {
      const classes = ["text-start"];

      if (!cell.value) {
        return classes;
      }

      classes.push("sortable");

      const options = (headerProps && headerProps.options) || {};
      const sortBy = options.sortBy || [];
      const sortDesc = options.sortDesc || [];

      const sortIndex = sortBy.indexOf(cell.value);
      if (sortIndex >= 0) {
        classes.push("active");
        classes.push(sortDesc[sortIndex] ? "desc" : "asc");
      }

      return classes;
    };

    // =>  METHODS
    const url_params = () => {
      return {
        start_date: startDate.value,
        end_date: endDate.value,
        site_ids: sites.value.join(','),
        ...(redacs.value ? { redac_ids: redacs.value.join(',') } : {}),
      };
    };

    const formatResult = (data) => data.map((item) => {
        const result = {
          ...item,
        };

        for (const [key, value] of Object.entries(item)) {
          if (['date'].includes(key)) {
            continue;
          }

          // Les compteurs de génération valent null quand un filtre rédacteur est
          // actif : aif_post_generation n'a pas de dimension rédacteur, on affiche
          // donc "n/a" plutôt qu'un 0 trompeur (formatNumber(null) rendrait "NaN").
          if (value === null || value === undefined) {
            result[key] = "n/a";
          } else if (['ca'].includes(key)) {
            result[key] = formatCurrency(value);
          } else if (RATIO_KEYS.includes(key)) {
            result[key] = formatPercent(value);
          } else {
            result[key] = formatNumber(value);
          }
        }

        return result;
      });

    const generateStats = (data) => {
      const tmp_stats = { ...STATS_DEFAULT };
      const generatedAvailable = data.some(
        (item) => item.nb_posts_generated !== null && item.nb_posts_generated !== undefined
      );

      for (const item of data) {
        for (const [key, value] of Object.entries(
          item
        )) {
          if (key in tmp_stats && value !== null && value !== undefined) {
            tmp_stats[key] += value;
          }
        }
      }

      if (!generatedAvailable) {
        for (const key of GENERATED_KEYS) {
          tmp_stats[key] = null;
        }
      }

      tmp_stats.post_draft_ratio =
        generatedAvailable && tmp_stats.nb_posts_generated > 0
          ? tmp_stats.nb_posts_published / tmp_stats.nb_posts_generated
          : null;
      tmp_stats.avg_posts_per_day = data.length
        ? tmp_stats.nb_posts_published / data.length
        : 0;

      for (const property of Object.keys(STATS_DEFAULT)) {
        const value = tmp_stats[property];

        if (value === null || value === undefined) {
          stats[property] = "n/a";
        } else if (['ca'].includes(property)) {
          stats[property] = formatCurrency(value);
        } else if (RATIO_KEYS.includes(property)) {
          stats[property] = formatPercent(value);
        } else {
          stats[property] = formatNumber(value);
        }
      }
    };

    const checkDates = () => {
      if (
        !startDate.value ||
        startDate.value.length !== 10 ||
        !endDate.value ||
        endDate.value.length !== 10
      ) {
        alert("Sélectionnez une plage de date !");
        return false;
      }

      return true;
    };

    const checkFilters = () => {
      if (!checkDates()) {
        return false;
      }

      if (sites.value.length === 0) {
        alert("Sélectionnez un site !");
        return false;
      }

      return true;
    };

    const exportExcel = async () => {
      if (!checkFilters()) {
        return;
      }

      page.loading = true;
      download.running = true;

      try {
        const { data } = await axiosGet(
          "/api/ai-factory/suivi-prod-ia/export_xlsx",
          url_params(),
          {
            responseType: "blob",
            onDownloadProgress: (progressEvent) => {
              download.percentCompleted = Math.round(
                (progressEvent.loaded * 100) / progressEvent.total
              );
            },
          }
        );

        excelButton.data = data;
      } catch (error) {
        console.error("error while fetching data");
        resetDownload(download);
      }

      page.loading = false;
    };

    const exportIaVsHumanExcel = async () => {
      // Cet export couvre tous les sites ayant des articles IA : il ne depend
      // pas du filtre site, seules les dates sont requises.
      if (!checkDates() || page.exportingIaVsHuman) {
        return;
      }

      page.exportingIaVsHuman = true;

      try {
        const { data } = await axiosGet(
          "/api/ai-factory/suivi-prod-ia/ia-vs-human/export_xlsx",
          {
            start_date: startDate.value,
            end_date: endDate.value,
          },
          { responseType: "blob" }
        );

        iaVsHumanExcel.data = data;
      } catch (error) {
        console.error("error while exporting ia vs human data");
      }

      page.exportingIaVsHuman = false;
    };

    const exportIaVsHumanAnnualExcel = async (year) => {
      if (page.exportingIaVsHumanAnnual) {
        return;
      }

      page.exportingIaVsHumanAnnual = true;

      try {
        const { data } = await axiosGet(
          "/api/ai-factory/suivi-prod-ia/ia-vs-human/export_annual_xlsx",
          { year },
          { responseType: "blob" }
        );

        const url = window.URL.createObjectURL(new Blob([data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
          "download",
          `comparatif-ia-humains-annuel-${year}.xlsx`
        );
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error("error while exporting annual ia vs human data", error);
      } finally {
        page.exportingIaVsHumanAnnual = false;
      }
    };

    const getIaVsHumanData = async () => {
      page.loadingIaVsHuman = true;
      page.iaVsHuman = {};

      try {
        const { data } = await axiosGet(
          "/api/ai-factory/suivi-prod-ia/ia-vs-human",
          {
            start_date: startDate.value,
            end_date: endDate.value,
            site_ids: sites.value.join(','),
          }
        );

        page.iaVsHuman = data;
      } catch (error) {
        console.error("error while fetching ia vs human data");
      }

      page.loadingIaVsHuman = false;
    };

    const getData = async () => {
      if (applyFilters.value && !page.loading) {
        if (!checkFilters()) {
          return;
        }
        page.loading = true;
        emit('dataUpdated', []);
        getIaVsHumanData();
        try {
          const { data } = await axiosGet(
            "/api/ai-factory/suivi-prod-ia",
            url_params()
          );

          page.items = formatResult(data);
          emit('dataUpdated', data);
          generateStats(data);
        } catch (error) {
          console.error("error while fetching data");
        }

        page.loading = false;

        // Force clicking on button again to re-run that request :
        // (we don't call this dispatch earlier to be sure, as state is async, that all listening components could run their requests)
        store.dispatch("common/updateApplyFilters", false);
      }
    };

    // =>  COMPUTED
    const applyFilters = computed(
      () => store.getters["common/getApplyFilters"]
    );
    const startDate = computed(() => store.getters["audience/getDates"][0]);
    const endDate = computed(() => store.getters["audience/getDates"][1]);
    const sites = computed(
      () => store.getters["audience/getSites"]
    );
    const redacs = computed(() => store.getters["audience/getRedacs"]);

    const exportDisabled = computed(
      () => page.loading || page.items.length === 0
    );

    // =>  WATCH
    watch(applyFilters, () => {
      getData();
    });

    watch(
      () => download.percentCompleted,
      (newValue) => {
        if (newValue >= 100) {
          setTimeout(() => {
            resetDownload(download);
          }, 500);
        }
      }
    );

    // =>  HOOKS

    // =>  DATA
    return {
      download,
      exportIaVsHumanExcel,
      exportIaVsHumanAnnualExcel,
      iaVsHumanExcel,
      headerRows,
      headerCellClass,
      sortIcon: mdiArrowUp,
      startDate,
      endDate,
      exportExcel,
      exportDisabled,
      page,
      stats,
      excelButton,
      headers,
    };
  },
};
</script>
